import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../../../context/LanguageContext';
import axios from '../../../utils/axios';
import { toast } from 'react-hot-toast';
import { FaTimes } from 'react-icons/fa';
// CustomerSearch import removed as it's no longer used
import ItemsList from './components/ItemsList';
// OrderTypeSection removed as it's handled by the backend
// PaymentMethodSection removed as payment method is determined by order kind

const EditOrderModal = ({ order, onClose, onUpdate }) => {
    const { language, t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [loadingOrderItems, setLoadingOrderItems] = useState(false);
    // Removed unused settings and search focus state
    const [formData, setFormData] = useState({
        payment_method: '',
        table_number: '',
        order_type: '',
        customer_id: null,
        delivery_fee: '',
        items: []
    });
    // Removed unused customer-related state
    const [items, setItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState('');

    // Removed unused customer-related functions

    const fetchItems = useCallback(async () => {
        try {
            const response = await axios.get('/items');
            const itemsData = response.data.data || [];        
            
            setItems(itemsData);
        } catch (error) {
            console.error('Error fetching items:', error);
            toast.error(language === 'ar' ? 'فشل تحميل المنتجات' : 'Failed to load items');
        }
    }, [language]);

    // Removed unused settings fetching

    // Fetch order items from the server
    const fetchOrderItems = useCallback(async (orderIdentifier, isCardOrder) => {
        try {
            setLoadingOrderItems(true);
            // Use the appropriate endpoint based on order type
            let endpoint;
            const orderTypeLabel = isCardOrder ? 'card' : 'cash';
            
            // Check if this is a card order number (starts with 9)
            const isCardOrderNumber = typeof orderIdentifier === 'string' && (orderIdentifier.startsWith('ORD9') || orderIdentifier.startsWith('9'));
            
            if (isCardOrder) {
                // For card orders, use the dedicated endpoint
                endpoint = `/card-orders/${orderIdentifier}/items`;
            } else {
                // For cash orders, use the regular endpoint with order_type parameter
                endpoint = `/orders/${orderIdentifier}/items?order_type=cash`;  
            }
            
            const response = await axios.get(endpoint);
            
            if (response.data && response.data.data) {
                
                const orderItems = response.data.data.map(item => {
                    // For existing items, we need to ensure we're using the correct item_id that will pass validation
                    // The backend validation requires item_id to exist in the items table
                    const itemId = parseInt(item.item_id || item.id);
                    
                    return {
                        id: itemId,
                        // Store the original item_id exactly as it came from the backend
                        item_id: item.item_id || item.id,
                        quantity: parseInt(item.quantity),
                        name: item.name,
                        name_ar: item.name_ar,
                        price: parseFloat(item.unit_price || item.price)
                    };
                });
                
                return orderItems;
            } else {
                return [];
            }
        } catch (error) {
            console.error('Error fetching order items:', error);
            toast.error(language === 'ar' ? 'فشل تحميل عناصر الطلب' : 'Failed to load order items');
            return [];
        } finally {
            setLoadingOrderItems(false);
        }
    }, [language]);

    useEffect(() => {
        if (order) {
            
            const isCardOrder = order.is_card_order === true || order.payment_method === 'card' || (order.unique_id && order.unique_id.startsWith('card-'));
            
            // Initialize form data with basic order info
            // We don't need to set order_type as it's handled by the backend
            // We only need to know if it's a card or cash order
            const orderKind = isCardOrder ? 'card' : 'cash';
            
            setFormData({
                order_kind: orderKind, // This tracks if it's a card or cash order
                customer_id: order.customer_id || null,
                items: [] // Will be populated after fetching items
            });
            
            // Fetch order items from the server
            const loadOrderData = async () => {
                // Fetch items first to ensure we have the full catalog
                await fetchItems();
                
                // Determine the best identifier to use
                // For card orders, always prefer order_number when it starts with 9 (card order pattern)
                const isCardOrderNumber = order.order_number && typeof order.order_number === 'string' && (order.order_number.startsWith('ORD9') || order.order_number.startsWith('9'));
                const identifier = isCardOrder && isCardOrderNumber ? order.order_number : order.id;
                
                // Fetch the order items
                const orderItems = await fetchOrderItems(identifier, isCardOrder);
                
                // Update form data with fetched items
                setFormData(prev => ({
                    ...prev,
                    items: orderItems
                }));
                
            };
            
            loadOrderData();
        }
    }, [order, fetchItems, fetchOrderItems]);

    // Removed unused customer loading effect

    // Removed unused customer-related functions

    // handleInputChange function removed as it's no longer needed
    // since we've removed the components that were using it

    const handleAddItem = useCallback(() => {
        if (!selectedItem) return;
        
        const item = items.find(i => i.id === parseInt(selectedItem));
        if (!item) return;

        const existingItem = formData.items.find(i => i.id === item.id);
        if (existingItem) {
            setFormData(prev => ({
                ...prev,
                items: prev.items.map(i => 
                    i.id === item.id 
                        ? { ...i, quantity: i.quantity + 1 }
                        : i
                )
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                items: [...prev.items, { 
                    id: item.id, // Keep the original ID format
                    // Store the original item_id from the database to ensure it passes validation
                    item_id: item.id,
                    quantity: 1,
                    name: item.name,
                    name_ar: item.name_ar,
                    price: parseFloat(item.price),
                    original_price: parseFloat(item.price), // Store original price for reference
                    // Store the database item reference to ensure we have the correct ID
                    db_item: item
                }]
            }));
        }
        setSelectedItem('');
    }, [selectedItem, items, formData.items]);

    const handleRemoveItem = useCallback((itemId) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter(item => item.id !== itemId)
        }));
    }, []);

    const handleUpdateQuantity = useCallback((itemId, newQuantity) => {
        if (newQuantity < 1) return;
        setFormData(prev => ({
            ...prev,
            items: prev.items.map(item =>
                item.id === itemId
                    ? { ...item, quantity: newQuantity }
                    : item
            )
        }));
    }, []);
    
    const handleUpdatePrice = useCallback((itemId, newPrice) => {
        if (newPrice < 0) return;
        setFormData(prev => ({
            ...prev,
            items: prev.items.map(item =>
                item.id === itemId
                    ? { ...item, price: newPrice }
                    : item
            )
        }));
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // We're only handling pickup orders now, so no need to validate table_service or delivery
        
        try {
            // Force order type to pickup
            let orderData = {
                // Force order type to pickup
                order_type: 'pickup',
                // Include items data with custom prices
                // The backend validation requires item_id to exist in the items table
                items: formData.items.map(item => {
                    
                    // Find the matching item in the database items array
                    // First try to use the stored db_item reference
                    let validItemId;
                    
                    if (item.db_item && item.db_item.id) {
                        // Use the stored database item reference
                        validItemId = item.db_item.id;
                    } else {
                        // Fallback: try to find the item in the items array
                        const matchingItem = items.find(i => 
                            i.id === item.id || 
                            i.name === item.name ||
                            (item.name_ar && i.name_ar === item.name_ar)
                        );
                        
                        if (matchingItem) {
                            validItemId = matchingItem.id;
                        } else {
                            // Last resort: use the first item ID as a fallback
                            validItemId = items.length > 0 ? items[0].id : null;
                        }
                    }
                    
                    return {
                        item_id: validItemId,
                        quantity: parseInt(item.quantity),
                        unit_price: parseFloat(item.price) // Send the custom price to the backend
                    };
                })
            };

            // Determine if this is a card order or cash order
            // Card orders have is_card_order: true flag, payment_method: "card", and unique_id that starts with "card-"
            // Cash orders have no is_card_order flag, payment_method: "cash", and unique_id that starts with "cash-"
            const isCardOrder = order.is_card_order === true || order.payment_method === 'card' || (order.unique_id && order.unique_id.startsWith('card-'));
            
            // Only include payment_method for card orders, as cash orders don't have this column
            if (isCardOrder) {
                orderData.payment_method = order.payment_method || 'card';
            }
                 
            let response;
            
            if (isCardOrder) {
                // For card orders, use the card orders API endpoint
                response = await axios.put(`sales-order-card/${order.id}`, orderData);
            } else {
                // For cash orders, use the regular orders API endpoint
                response = await axios.put(`orders/${order.id}`, orderData);
            }
            
            const updatedOrder = response.data.data || response.data;
            // Let the parent component handle the success toast
            await onUpdate(updatedOrder);
            onClose();
        } catch (error) {
            console.error('Error updating order:', error);
            console.error('Error response:', error.response?.data);
            
            // Show more detailed error message if available
            const errorMessage = error.response?.data?.message || t('admin:update_error');
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

                <div className="inline-block transform rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className={`flex justify-between items-center mb-4 ${language === 'ar' ? 'flex-row-reverse' : 'flex-row'}`}>
                            <h3 className={`text-lg font-medium text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                {language === 'ar' ? 'تعديل الطلب' : 'Edit Order'}
                            </h3>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                                <FaTimes className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Order type is managed by backend */}

                            {/* Delivery customer selection removed as it's handled by backend */}

                            {/* Display order kind (card/cash) as read-only */}
                            <div dir={language === 'ar' ? 'rtl' : 'ltr'}>
                                <label className={`block text-sm font-medium text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'نوع الطلب' : 'Order Kind'}
                                </label>
                                <div className={`mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {formData.order_kind === 'card' ? 
                                        (language === 'ar' ? 'بطاقة' : 'Card') : 
                                        (language === 'ar' ? 'نقداً' : 'Cash')}
                                </div>
                            </div>

                            <div>
                                <label className={`block text-sm font-medium text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'المنتجات' : 'Items'}
                                </label>
                                
                                {loadingOrderItems ? (
                                    <div className="mt-2 text-center py-4">
                                        <p className="text-gray-500">{language === 'ar' ? 'جاري تحميل المنتجات...' : 'Loading items...'}</p>
                                    </div>
                                ) : (
                                    <ItemsList 
                                        items={items}
                                        formItems={formData.items}
                                        selectedItem={selectedItem}
                                        setSelectedItem={setSelectedItem}
                                        handleAddItem={handleAddItem}
                                        handleRemoveItem={handleRemoveItem}
                                        handleUpdateQuantity={handleUpdateQuantity}
                                        handleUpdatePrice={handleUpdatePrice}
                                    />
                                )}
                            </div>

                            <div className={`mt-5 sm:mt-4 sm:flex ${language === 'ar' ? 'sm:flex-row' : 'sm:flex-row-reverse'}`}>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`inline-flex w-full justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 ${language === 'ar' ? 'sm:ml-3 text-right' : 'sm:ml-3 text-left'} sm:w-auto sm:text-sm disabled:opacity-50`}
                                >
                                    {loading
                                        ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                                        : (language === 'ar' ? 'حفظ التغييرات' : 'Save Changes')}
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className={`mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}
                                >
                                    {language === 'ar' ? 'إلغاء' : 'Cancel'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditOrderModal;